import "../Overview/EndpointOverview.css";
import "./Account.css";
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import {
    Mail,
    Clock,
    Shield,
    Building2,
    CheckCircle,
    KeyRound,
    UserCog,
    BadgeCheck,
    Fingerprint,
} from "lucide-react";
import useUserStore from "../../../services/userStore";
import { myLocalStorage } from "../../StorageHelper";
import OverviewCardHeader from "../Overview/OverviewCardHeader";

function AccountField({ icon: Icon, label, children }: { icon: LucideIcon; label: string; children: ReactNode }) {
    return (
        <div className="account-field">
            <span className="account-field-icon" aria-hidden="true"><Icon /></span>
            <div className="account-field-copy">
                <span className="account-field-label">{label}</span>
                <div className="account-field-value">{children}</div>
            </div>
        </div>
    );
}

export default function AccountPage() {
    const realm = myLocalStorage.getItem("realm") || "";

    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;

    const { email, role, userRole, userRoles } = useUserStore((state) => state.user);

    const effectiveRole = Array.isArray(userRoles) ? userRoles : userRoles ?? userRole ?? "";

    const hasRole = (roleName: string) => {
        if (Array.isArray(effectiveRole)) return effectiveRole.includes(roleName);
        if (typeof effectiveRole === "string") return effectiveRole.includes(roleName);
        return false;
    };

    const getPermission = () => {
        if (hasRole("whiteswan-admin") || role === "SYS_ADMIN") {
            return ["Full Access", "User Management", "Request Approvals", "System Configuration", "Audit Logs"]
        } else if (hasRole("server-pam-admin")) {
            return ["Server Machine Access", "Server User Management", "Server Request Approvals", "System Configuration", "Server Audit Logs"]
        } else if (hasRole("epam-admin")) {
            return ["Endpoint Machine Access", "Endpoint User Management", "Endpoint Request Approvals", "System Configuration", "Endpoint Audit Logs"]
        } else if (hasRole("epam-user")) {
            return ["Endpoint Resource Request", "Cloud Resource Request"]
        } else {
            return ["Servers Connect Request", "Servers Resource Request", "Cloud Resource Request"]
        }
    }

    const accountData = {
        permissions: getPermission()
    };

    const roles: string[] = Array.isArray(userRoles)
        ? userRoles.map((r: string) => String(r).trim()).filter((r: string) => r)
        : typeof (userRoles || userRole) === "string"
            ? (userRoles || userRole || "").split(',').map((r: string) => r.trim()).filter((r: string) => r)
            : [];

    const authenticationLabel = realm.includes("legacy")
        ? "MFA Authentication"
        : role === "SYS_ADMIN"
            ? "Strong Password"
            : "Passkey Authentication";

    return (
        <div className="endpoint-overview account-page">
            <Card className="overview-card account-hero">
                <div className="account-identity">
                    <span className="account-avatar" aria-hidden="true">WS</span>
                    <div className="account-identity-copy">
                        <div className="account-name-row">
                            <h1 className="account-name" title={email}>{email}</h1>
                            <span className="account-status">
                                <CheckCircle aria-hidden="true" />
                                Active
                            </span>
                        </div>
                        <div className="account-role-list">
                            {roles.map((roleName, index) => (
                                <span key={index} className="account-role">
                                    <Shield aria-hidden="true" />
                                    {roleName}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="account-hero-stats">
                    <div className="account-stat">
                        <span className="account-stat-icon" aria-hidden="true"><Building2 /></span>
                        <div className="min-w-0">
                            <span className="account-stat-value" title={tenantName}>{tenantName}</span>
                            <span className="account-stat-label">Tenant Name</span>
                        </div>
                    </div>
                    <div className="account-stat" data-tone="violet">
                        <span className="account-stat-icon" aria-hidden="true"><KeyRound /></span>
                        <div className="min-w-0">
                            <span className="account-stat-value">{accountData.permissions.length}</span>
                            <span className="account-stat-label">Permissions</span>
                        </div>
                    </div>
                </div>
            </Card>

            <div className="account-grid">
                <Card className="overview-card">
                    <OverviewCardHeader
                        fromdetails
                        title="Contact Information"
                        description="How your account is identified across the platform"
                        icon={Mail}
                    />
                    <CardContent className="overview-card-content account-fields">
                        <AccountField icon={Mail} label="Email Address">
                            {email}
                        </AccountField>
                        <AccountField icon={Building2} label="Tenant Name">
                            {tenantName}
                        </AccountField>
                        <AccountField icon={UserCog} label={roles.length > 1 ? "Roles" : "Role"}>
                            {roles.length > 1 ? (
                                <span className="account-role-list">
                                    {roles.map((roleName, index) => (
                                        <span key={index} className="account-role">{roleName}</span>
                                    ))}
                                </span>
                            ) : (
                                roles[0] || ''
                            )}
                        </AccountField>
                    </CardContent>
                </Card>

                <Card className="overview-card">
                    <OverviewCardHeader
                        fromdetails
                        title="Security Information"
                        description="Authentication and session settings for your account"
                        icon={Fingerprint}
                    />
                    <CardContent className="overview-card-content account-fields">
                        <AccountField icon={BadgeCheck} label={authenticationLabel}>
                            <span className="account-enabled">
                                <CheckCircle aria-hidden="true" />
                                Enabled
                            </span>
                        </AccountField>
                        <AccountField icon={Clock} label="Session Timeout">
                            8 hours
                        </AccountField>
                    </CardContent>
                </Card>
            </div>

            <Card className="overview-card">
                <OverviewCardHeader
                    fromdetails
                    title="Access Permissions"
                    description="Your account has the following permissions within the White Swan Security platform"
                    icon={Shield}
                    detail={`${accountData.permissions.length} granted`}
                />
                <CardContent className="overview-card-content">
                    <div className="account-permissions">
                        {accountData.permissions.map((permission, index) => (
                            <div key={index} className="account-permission">
                                <span className="account-permission-icon" aria-hidden="true">
                                    <CheckCircle />
                                </span>
                                <span className="account-permission-name">{permission}</span>
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
